export const SUBTITLE_TEXT_TRANSFORMS = [
  'none',
  'uppercase',
  'lowercase',
  'small-caps',
] as const;

export type SubtitleTextTransform = (typeof SUBTITLE_TEXT_TRANSFORMS)[number];

export const DEFAULT_SUBTITLE_TEXT_TRANSFORM: SubtitleTextTransform = 'uppercase';

export function normalizeSubtitleTextTransform(
  value: unknown,
  fallback: SubtitleTextTransform = DEFAULT_SUBTITLE_TEXT_TRANSFORM,
): SubtitleTextTransform {
  if (typeof value === 'string' && (SUBTITLE_TEXT_TRANSFORMS as readonly string[]).includes(value)) {
    return value as SubtitleTextTransform;
  }
  return fallback;
}

export function readKitSubtitleTextTransform(
  sub: object | null | undefined,
  fallback: SubtitleTextTransform = DEFAULT_SUBTITLE_TEXT_TRANSFORM,
): SubtitleTextTransform {
  const value = sub && 'text_transform' in sub
    ? (sub as { text_transform?: unknown }).text_transform
    : undefined;
  return normalizeSubtitleTextTransform(value, fallback);
}

export interface SubtitleTextTransformCss {
  textTransform: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  fontVariant?: 'normal' | 'small-caps';
}

/** Maps persisted subtitle letter-case to valid CSS for Remotion/browser render. */
export function subtitleTextTransformToCss(value: unknown): SubtitleTextTransformCss {
  const transform = normalizeSubtitleTextTransform(value);

  switch (transform) {
    case 'none':
      return { textTransform: 'none', fontVariant: 'normal' };
    case 'uppercase':
      return { textTransform: 'uppercase', fontVariant: 'normal' };
    case 'lowercase':
      return { textTransform: 'lowercase', fontVariant: 'normal' };
    case 'small-caps':
      // Each subtitle word is its own element. Capitalize first normalizes
      // ALL-CAPS transcript tokens to "Imagina", then small-caps keeps the
      // leading letter at full cap height and shrinks the rest.
      return { textTransform: 'capitalize', fontVariant: 'small-caps' };
    default:
      return { textTransform: 'uppercase', fontVariant: 'normal' };
  }
}
